/* #region header */
/**************************************************************************************************
//
//  Description: Aligned Assets Action Button component
//
//  Copyright:    © 2021 Aligned Assets Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier            Issue# Description
//#region Version 1.0.0.0 changes
//    001   02.06.21 Sean Flook         WI39345 Initial Revision.
//    002   02.06.21 Sean Flook         WI39345 Added home and close action buttons.
//    003   08.06.21 Sean Flook         WI39345 Changed the close icon.
//    004   16.06.21 Sean Flook         WI39345 Added further parameters to control how the buttons are displayed.
//    005   28.06.21 Sean Flook         WI39345 Added disabled colour to icons.
//    006   07.12.23 Sean Flook                 Migrated MUI to latest version.
//    007   12.12.23 Sean Flook                 Fix MUI for React 18
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
/* #endregion header */

/* #region imports */

import React, { useState } from "react";
import PropTypes from "prop-types";
import { IconButton, Tooltip, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PreviousIcon from "@mui/icons-material/ChevronLeft";
import NextIcon from "@mui/icons-material/ChevronRight";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/ArrowBack";
import { useTheme } from "@mui/system";

/* #endregion imports */

AAActionButton.propTypes = {
  variant: PropTypes.string.isRequired,
  buttonLabel: PropTypes.string,
  disabled: PropTypes.bool,
  tooltipTitle: PropTypes.string,
  tooltipPlacement: PropTypes.string,
  fontSize: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

function AAActionButton({ variant, buttonLabel, disabled, tooltipTitle, tooltipPlacement, fontSize, onClick }) {
  const theme = useTheme();

  const [iconHover, setIconHover] = useState(false);

  const handleIconMouseEnter = () => {
    setIconHover(true);
  };

  const handleIconMouseLeave = () => {
    setIconHover(false);
  };

  const handleClickEvent = () => {
    if (onClick) onClick();
  };

  const renderIcon = () => {
    let iconComp = null;

    switch (variant) {
      case "previous":
        if (fontSize && fontSize !== "") {
          iconComp = (
            <PreviousIcon
              fontSize={fontSize}
              sx={
                disabled
                  ? { color: theme.palette.text.disabled }
                  : !iconHover
                  ? { color: "#535353" }
                  : { color: "#2A6EBB" }
              }
            />
          );
        } else {
          iconComp = (
            <PreviousIcon
              sx={
                disabled
                  ? { color: theme.palette.text.disabled }
                  : !iconHover
                  ? { color: "#535353" }
                  : { color: "#2A6EBB" }
              }
            />
          );
        }

        break;

      case "next":
        if (fontSize && fontSize !== "") {
          iconComp = (
            <NextIcon
              fontSize={fontSize}
              sx={
                disabled
                  ? { color: theme.palette.text.disabled }
                  : !iconHover
                  ? { color: "#535353" }
                  : { color: "#2A6EBB" }
              }
            />
          );
        } else {
          iconComp = (
            <NextIcon
              sx={
                disabled
                  ? { color: theme.palette.text.disabled }
                  : !iconHover
                  ? { color: "#535353" }
                  : { color: "#2A6EBB" }
              }
            />
          );
        }
        break;

      case "save":
        if (fontSize && fontSize !== "") {
          iconComp = (
            <SaveIcon
              fontSize={fontSize}
              sx={
                disabled
                  ? { color: theme.palette.text.disabled }
                  : !iconHover
                  ? { color: "#535353" }
                  : { color: "#2A6EBB" }
              }
            />
          );
        } else {
          iconComp = (
            <SaveIcon
              sx={
                disabled
                  ? { color: theme.palette.text.disabled }
                  : !iconHover
                  ? { color: "#535353" }
                  : { color: "#2A6EBB" }
              }
            />
          );
        }
        break;

      case "delete":
        if (fontSize && fontSize !== "") {
          iconComp = (
            <DeleteIcon
              fontSize={fontSize}
              sx={
                disabled
                  ? { color: theme.palette.text.disabled }
                  : !iconHover
                  ? { color: "#535353" }
                  : { color: "#2A6EBB" }
              }
            />
          );
        } else {
          iconComp = (
            <DeleteIcon
              sx={
                disabled
                  ? { color: theme.palette.text.disabled }
                  : !iconHover
                  ? { color: "#535353" }
                  : { color: "#2A6EBB" }
              }
            />
          );
        }
        break;

      case "return":
      case "home":
        if (fontSize && fontSize !== "") {
          iconComp = (
            <HomeIcon
              fontSize={fontSize}
              sx={
                disabled
                  ? { color: theme.palette.text.disabled }
                  : !iconHover
                  ? { color: "#535353" }
                  : { color: "#2A6EBB" }
              }
            />
          );
        } else {
          iconComp = (
            <HomeIcon
              sx={
                disabled
                  ? { color: theme.palette.text.disabled }
                  : !iconHover
                  ? { color: "#535353" }
                  : { color: "#2A6EBB" }
              }
            />
          );
        }
        break;

      case "close":
        if (fontSize && fontSize !== "") {
          iconComp = (
            <CloseIcon
              fontSize={fontSize}
              sx={
                disabled
                  ? { color: theme.palette.text.disabled }
                  : !iconHover
                  ? { color: "#535353" }
                  : { color: "#2A6EBB" }
              }
            />
          );
        } else {
          iconComp = (
            <CloseIcon
              sx={
                disabled
                  ? { color: theme.palette.text.disabled }
                  : !iconHover
                  ? { color: "#535353" }
                  : { color: "#2A6EBB" }
              }
            />
          );
        }
        break;

      default:
        if (fontSize && fontSize !== "") {
          iconComp = (
            <AddCircleOutlineIcon
              fontSize={fontSize}
              sx={
                disabled
                  ? { color: theme.palette.text.disabled }
                  : !iconHover
                  ? { color: "#535353" }
                  : { color: "#2A6EBB" }
              }
            />
          );
        } else {
          iconComp = (
            <AddCircleOutlineIcon
              sx={
                disabled
                  ? { color: theme.palette.text.disabled }
                  : !iconHover
                  ? { color: "#535353" }
                  : { color: "#2A6EBB" }
              }
            />
          );
        }
        break;
    }

    return (
      <IconButton
        size="small"
        disabled={disabled}
        onClick={handleClickEvent}
        onMouseEnter={handleIconMouseEnter}
        onMouseLeave={handleIconMouseLeave}
        aria-label={variant}
      >
        <Tooltip title={tooltipTitle} arrow placement={tooltipPlacement}>
          {iconComp}
        </Tooltip>
        {buttonLabel && buttonLabel.length > 0 && (
          <Typography
            variant="body2"
            sx={{
              pl: theme.spacing(0.5),
              pr: theme.spacing(1),
              color: "#535353",
            }}
          >
            {buttonLabel}
          </Typography>
        )}
      </IconButton>
    );
  };

  return renderIcon();
}

export default AAActionButton;
